<template>
  <Dialog
    :visible="true"
    modal
    header="Righty wants to make changes"
    :style="{ width: '32rem' }"
    :closable="true"
    @update:visible="$emit('cancel')"
  >
    <div class="space-y-4">
      <!-- Summary List -->
      <div class="space-y-2">
        <div
          v-for="(summary, idx) in actionSummaries"
          :key="idx"
          class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
        >
          <!-- Expandable header -->
          <button
            type="button"
            class="flex w-full items-center justify-between p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            @click="toggleExpand(idx)"
          >
            <div class="flex items-center gap-2">
              <SparklesIcon class="h-4 w-4 text-indigo-500" />
              <span class="font-medium text-slate-700 dark:text-slate-200">
                {{ summary.title }}
              </span>
            </div>
            <ChevronDownIcon
              class="h-4 w-4 text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': expandedSections.has(idx) }"
            />
          </button>

          <!-- Expanded details -->
          <div
            v-if="expandedSections.has(idx)"
            class="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2"
          >
            <p class="text-sm text-slate-600 dark:text-slate-300 mb-2">
              {{ summary.description }}
            </p>
            <div v-if="summary.items?.length" class="space-y-1">
              <div
                v-for="(item, itemIdx) in summary.items"
                :key="itemIdx"
                class="flex items-start gap-2 text-sm"
              >
                <span class="text-slate-400">-</span>
                <span class="text-slate-600 dark:text-slate-300">
                  <strong class="font-medium">{{ item.label }}:</strong>
                  {{ item.value }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Raw JSON Preview (collapsible) -->
      <div class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <button
          type="button"
          class="flex w-full items-center justify-between p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm"
          @click="showRawJson = !showRawJson"
        >
          <span class="text-slate-500 dark:text-slate-400">View raw action data</span>
          <ChevronDownIcon
            class="h-4 w-4 text-slate-400 transition-transform duration-200"
            :class="{ 'rotate-180': showRawJson }"
          />
        </button>
        <div
          v-if="showRawJson"
          class="border-t border-slate-200 dark:border-slate-700"
        >
          <pre
            class="max-h-48 overflow-auto bg-slate-900 p-3 text-xs text-slate-100"
          >{{ JSON.stringify(action.data, null, 2) }}</pre>
        </div>
      </div>

      <!-- Warning -->
      <div class="flex items-start gap-2 rounded-lg bg-amber-50 p-3 dark:bg-amber-900/30">
        <ExclamationTriangleIcon class="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <p class="text-sm text-amber-800 dark:text-amber-200">
          This will modify your RightMessage configuration. You can undo changes from the snapshot history.
        </p>
      </div>

      <!-- Error message -->
      <div
        v-if="errorMessage"
        class="flex items-start gap-2 rounded-lg bg-red-50 p-3 dark:bg-red-900/30"
      >
        <XCircleIcon class="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
        <p class="text-sm text-red-800 dark:text-red-200">
          {{ errorMessage }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button label="Cancel" severity="secondary" text @click="$emit('cancel')" />
        <Button
          label="Apply Changes"
          severity="primary"
          icon="pi pi-check"
          :loading="isApplying"
          @click="applyChanges"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  XCircleIcon,
  SparklesIcon
} from '@heroicons/vue/24/outline'
import { useNotificationStore } from '@/stores/notificationStore'
import type { RightyAction } from '../types'
import { executeAction, summarizeAction, type ActionSummary } from '../services/rightyActionHandler'

const props = defineProps<{
  action: RightyAction
}>()

const emit = defineEmits<{
  approve: [createdIds: Record<string, string>]
  cancel: []
}>()

const notificationStore = useNotificationStore()

// State
const expandedSections = ref<Set<number>>(new Set([0])) // First section expanded by default
const showRawJson = ref(false)
const isApplying = ref(false)
const errorMessage = ref<string | null>(null)

// Computed
const actionSummaries = computed<ActionSummary[]>(() => {
  return summarizeAction(props.action)
})

// Methods
function toggleExpand(idx: number) {
  if (expandedSections.value.has(idx)) {
    expandedSections.value.delete(idx)
  } else {
    expandedSections.value.add(idx)
  }
  // Force reactivity update
  expandedSections.value = new Set(expandedSections.value)
}

async function applyChanges() {
  isApplying.value = true
  errorMessage.value = null

  try {
    const result = await executeAction(props.action)

    if (result.success) {
      notificationStore.addNotification('Changes applied successfully!')
      emit('approve', result.createdIds)
    } else {
      errorMessage.value = result.error || 'Failed to apply changes'
    }
  } catch (err: any) {
    errorMessage.value = err.message || 'An unexpected error occurred'
  } finally {
    isApplying.value = false
  }
}
</script>
